/**
 * 新規登録ページ - Retro-Brutalistic デザインシステム
 * Step1: アカウント情報入力（メール・パスワード・氏名）
 * Step2: 利用形態選択（個人 or 組織）
 * Step3: 確認・登録完了
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signUp, confirmSignUp } from '../services/auth';

// ヒーローパネルの背景画像（Login と共通）
const HERO_IMAGE_URL =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuAm0Fh6WzAZ_yggLAtL3RrAStF6FHoAm-7Bsn0Hs5Ai5cRIkWtWOEfg6XvElgh9zykmJfli8xS81rVqEyxZHmOy1uz58BOrN3PB16qiR9g0MyQgXExFrkhl6prH_iGC-8IuQKXahq8Ns7akx34lBaUHkSH2agT2G_DncD5dYw4tW-ISQeodKtBOeMnAoAAsj5bIlJ6CyYbEYT3t4FDqAmP5afe2Y8bsZtm99RJANJBl_fml8LhSap-5pwQuM27QH2zQDsv0BqhM5kDG';

// ===== 共通 UI コンポーネント =====

function ErrorAlert({ message }) {
  if (!message) return null;
  return (
    <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-error-container border-2 border-[#ba1a1a]">
      <span className="material-symbols-outlined text-[#93000a] text-xl flex-shrink-0 mt-0.5">error</span>
      <p className="text-sm text-[#93000a]">{message}</p>
    </div>
  );
}

function FieldLabel({ htmlFor, children }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block"
      style={{
        fontFamily: 'Space Grotesk, sans-serif',
        fontSize: '12px',
        fontWeight: 700,
        letterSpacing: '0.1em',
        color: '#414751',
        textTransform: 'uppercase',
      }}
    >
      {children}
    </label>
  );
}

function AeroInput({ id, type = 'text', value, onChange, placeholder, autoFocus, icon, maxLength, inputClassName }) {
  return (
    <div className="relative">
      {icon && (
        <span
          className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 select-none pointer-events-none"
          style={{ color: '#717782', fontSize: '20px' }}
        >
          {icon}
        </span>
      )}
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoFocus={autoFocus}
        maxLength={maxLength}
        required
        className={`w-full py-3 bg-surface border-2 border-outline-variant rounded-lg font-body text-body-md placeholder:text-outline transition-colors focus:outline-none focus:border-primary ${inputClassName || ''}`}
        style={{
          paddingLeft: icon ? '2.5rem' : '1rem',
          paddingRight: '1rem',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)',
        }}
      />
    </div>
  );
}

function PasswordInput({ id, value, onChange, placeholder }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <span
        className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 select-none pointer-events-none"
        style={{ color: '#717782', fontSize: '20px' }}
      >
        lock
      </span>
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required
        className="w-full py-3 bg-surface border-2 border-outline-variant rounded-lg font-body text-body-md placeholder:text-outline transition-colors focus:outline-none focus:border-primary"
        style={{
          paddingLeft: '2.5rem',
          paddingRight: '3rem',
          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.05)',
        }}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors focus:outline-none"
        style={{ color: '#717782' }}
        onMouseEnter={(e) => (e.currentTarget.style.color = '#00508e')}
        onMouseLeave={(e) => (e.currentTarget.style.color = '#717782')}
        tabIndex={-1}
      >
        <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
          {visible ? 'visibility_off' : 'visibility'}
        </span>
      </button>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

function QuestWindow({ children }) {
  return (
    <div
      className="relative bg-surface-container-lowest border-4 border-primary rounded-xl p-8"
      style={{ boxShadow: '0 8px 0 0 #00508e' }}
    >
      <div className="absolute inset-2 border-2 border-surface-variant rounded-lg pointer-events-none" />
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
}

function AeroButton({ type = 'button', onClick, disabled, isLoading, children, variant = 'primary' }) {
  const isPrimary = variant === 'primary';
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || isLoading}
      className="w-full text-on-primary font-headline text-lg py-4 rounded-full flex items-center justify-center gap-2 group transition-all disabled:opacity-60 disabled:cursor-not-allowed"
      style={{
        backgroundColor: isPrimary ? '#0068b7' : '#e6e8ef',
        color: isPrimary ? '#ffffff' : '#00508e',
        boxShadow: isPrimary ? '0 6px 0 0 #00508e' : '0 6px 0 0 #c1c7d3',
        fontFamily: 'Space Grotesk, sans-serif',
        fontWeight: 600,
      }}
      onMouseEnter={(e) => {
        if (!disabled && !isLoading) {
          e.currentTarget.style.boxShadow = isPrimary ? '0 8px 0 0 #00508e' : '0 8px 0 0 #c1c7d3';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = isPrimary ? '0 6px 0 0 #00508e' : '0 6px 0 0 #c1c7d3';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
      onMouseDown={(e) => {
        if (!disabled && !isLoading) {
          e.currentTarget.style.boxShadow = 'none';
          e.currentTarget.style.transform = 'translateY(6px)';
        }
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.boxShadow = isPrimary ? '0 6px 0 0 #00508e' : '0 6px 0 0 #c1c7d3';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {isLoading ? <Spinner /> : children}
    </button>
  );
}

// ===== ページ共通レイアウト =====

function PageShell({ children }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ backgroundColor: '#d3e4ff' }}
    >
      <div
        className="absolute inset-0 z-0 pointer-events-none opacity-40 mix-blend-overlay"
        style={{ backgroundImage: "url('https://www.transparenttextures.com/patterns/stardust.png')" }}
      />
      <div
        className="relative z-10 w-full max-w-6xl flex rounded-2xl overflow-hidden border-4 border-primary bg-surface-container-lowest"
        style={{
          height: 'min(860px, 92vh)',
          boxShadow: '0 8px 32px rgba(0,80,142,0.2)',
        }}
      >
        {/* 左パネル: ヒーロー画像 */}
        <div
          className="hidden lg:flex w-1/2 relative border-r-4 border-primary"
          style={{
            backgroundImage: `url('${HERO_IMAGE_URL}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(to top, rgba(0,80,142,0.85) 0%, transparent 60%)' }}
          />
          <div className="absolute bottom-12 left-12 right-12 text-white">
            <span
              className="material-symbols-outlined mb-4 block"
              style={{ fontSize: '48px', fontVariationSettings: "'FILL' 1", color: '#fff' }}
            >
              rocket_launch
            </span>
            <h1
              className="text-headline-lg mb-2 tracking-tight"
              style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#fff' }}
            >
              AIRGROW
            </h1>
            <p
              className="text-body-lg max-w-md"
              style={{ color: 'rgba(255,255,255,0.9)', fontFamily: 'Inter, sans-serif' }}
            >
              次世代ドローン管制システム。<br />
              高精度なテレメトリとミッション管理を一つのコンソールで。
            </p>
          </div>
        </div>

        {/* 右パネル: フォーム */}
        <div
          className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-surface-container-lowest relative overflow-y-auto"
          style={{ backgroundImage: "url('https://www.transparenttextures.com/patterns/az-subtle.png')" }}
        >
          <div className="absolute -top-12 -left-12 pointer-events-none select-none opacity-5">
            <span className="material-symbols-outlined text-primary" style={{ fontSize: '96px' }}>
              radar
            </span>
          </div>
          <div className="w-full max-w-md">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== ステップインジケーター =====

const STEP_LABELS = ['アカウント情報', 'パスワード', '利用形態', 'メール確認'];

function StepIndicator({ step }) {
  return (
    <div className="flex items-center mb-8">
      {STEP_LABELS.map((label, i) => {
        const stepNum = i + 1;
        const isDone = step > stepNum;
        const isCurrent = step === stepNum;
        return (
          <React.Fragment key={i}>
            <div className="flex flex-col items-center gap-1 w-20 flex-shrink-0">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                style={{
                  backgroundColor: isDone ? '#0068b7' : isCurrent ? '#d3e4ff' : '#e6e8ef',
                  border: isCurrent ? '3px solid #0068b7' : isDone ? 'none' : '2px solid #c1c7d3',
                  color: isDone ? '#fff' : isCurrent ? '#00508e' : '#717782',
                  fontFamily: 'Space Grotesk, sans-serif',
                  boxShadow: isDone ? '0 3px 0 0 #00508e' : 'none',
                }}
              >
                {isDone
                  ? <span className="material-symbols-outlined" style={{ fontSize: '16px', fontVariationSettings: "'FILL' 1" }}>check</span>
                  : stepNum
                }
              </div>
              <span
                className="text-xs whitespace-nowrap"
                style={{
                  fontFamily: 'Space Grotesk, sans-serif',
                  fontWeight: isCurrent ? 700 : 400,
                  color: isCurrent ? '#00508e' : '#717782',
                  letterSpacing: '0.05em',
                }}
              >
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div
                className="flex-1 h-0.5 mb-5"
                style={{ backgroundColor: step > i + 1 ? '#0068b7' : '#c1c7d3' }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ===== メインコンポーネント =====

const STEPS = {
  ACCOUNT: 1,
  PASSWORD: 2,
  ORG_TYPE: 3,
  VERIFY: 4,
  COMPLETE: 5,
};

export default function Register() {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.ACCOUNT);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    orgType: '',
    orgName: '',
    verificationCode: '',
  });

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const passwordChecks = {
    length: form.password.length >= 8,
    uppercase: /[A-Z]/.test(form.password),
    lowercase: /[a-z]/.test(form.password),
    number: /[0-9]/.test(form.password),
    match: form.password === form.confirmPassword && form.confirmPassword.length > 0,
  };
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);
  const isAccountInfoValid = Boolean(form.email && form.name);

  const handleAccountNext = (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      setError('すべての項目を入力してください');
      return;
    }
    setStep(STEPS.PASSWORD);
  };

  const handlePasswordNext = (e) => {
    e.preventDefault();
    if (!isPasswordValid) {
      setError('パスワードの要件を満たしてください');
      return;
    }
    setStep(STEPS.ORG_TYPE);
  };

  const handleOrgTypeNext = async (e) => {
    e.preventDefault();
    if (!form.orgType) {
      setError('利用形態を選択してください');
      return;
    }
    if (form.orgType === 'organization' && !form.orgName.trim()) {
      setError('組織名を入力してください');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      await signUp({
        email: form.email,
        password: form.password,
        name: form.name,
        orgType: form.orgType,
      });
      setStep(STEPS.VERIFY);
    } catch (err) {
      if (err.code === 'UsernameExistsException') {
        setError('このメールアドレスはすでに登録されています');
      } else {
        setError(err.message || '登録に失敗しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!form.verificationCode) {
      setError('確認コードを入力してください');
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      await confirmSignUp(form.email, form.verificationCode);
      sessionStorage.setItem('pendingRegistration', JSON.stringify({
        name: form.name,
        orgType: form.orgType,
        orgName: form.orgType === 'organization' ? form.orgName : form.name,
      }));
      setStep(STEPS.COMPLETE);
    } catch (err) {
      if (err.code === 'CodeMismatchException') {
        setError('確認コードが正しくありません');
      } else if (err.code === 'ExpiredCodeException') {
        setError('確認コードの有効期限が切れています。再送信してください');
      } else {
        setError(err.message || '確認に失敗しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    setError('');
    try {
      const { resendConfirmationCode } = await import('../services/auth');
      await resendConfirmationCode(form.email);
      alert('確認コードを再送信しました');
    } catch (err) {
      setError('再送信に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // ===== Step1: アカウント情報 =====
  if (step === STEPS.ACCOUNT) {
    return (
      <PageShell>
        <QuestWindow>
          {/* ヘッダ */}
          <div className="flex flex-col items-center mb-6">
            <div
              className="p-4 rounded-full mb-4"
              style={{ backgroundColor: '#0068b7', boxShadow: '0 4px 0 0 #00508e' }}
            >
              <span
                className="material-symbols-outlined text-white"
                style={{ fontSize: '28px', fontVariationSettings: "'FILL' 1" }}
              >
                person_add
              </span>
            </div>
            <h2
              className="text-headline-md text-primary tracking-tight"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              新規登録
            </h2>
            <div className="h-0.5 w-16 mt-2 border-b-2 border-primary" />
          </div>

          <StepIndicator step={step} />

          <form onSubmit={handleAccountNext} className="space-y-4">
            <ErrorAlert message={error} />

            <div className="space-y-2">
              <FieldLabel htmlFor="name">氏名</FieldLabel>
              <AeroInput
                id="name"
                type="text"
                value={form.name}
                onChange={(e) => updateForm('name', e.target.value)}
                placeholder="例：岡崎 年弘"
                autoFocus
                icon="person"
              />
            </div>

            <div className="space-y-2">
              <FieldLabel htmlFor="email">メールアドレス</FieldLabel>
              <AeroInput
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => updateForm('email', e.target.value)}
                placeholder="example@nttedt.co.jp"
                icon="mail"
              />
            </div>

            <div className="pt-2">
              <AeroButton type="submit" disabled={!isAccountInfoValid}>
                <span>次へ</span>
                <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform" style={{ fontSize: '20px' }}>
                  arrow_forward
                </span>
              </AeroButton>
            </div>
          </form>

          <div className="mt-6 text-center border-t-2 border-dashed border-outline-variant pt-5">
            <p className="text-body-md text-on-surface-variant" style={{ fontFamily: 'Inter, sans-serif' }}>
              すでにアカウントをお持ちの方は{' '}
              <Link to="/login" className="text-primary font-medium hover:underline decoration-2 underline-offset-4">
                ログイン
              </Link>
            </p>
          </div>
        </QuestWindow>
      </PageShell>
    );
  }

  // ===== Step2: パスワード設定 =====
  if (step === STEPS.PASSWORD) {
    return (
      <PageShell>
        <QuestWindow>
          {/* ヘッダ */}
          <div className="flex flex-col items-center mb-6">
            <div
              className="p-4 rounded-full mb-4"
              style={{ backgroundColor: '#0068b7', boxShadow: '0 4px 0 0 #00508e' }}
            >
              <span
                className="material-symbols-outlined text-white"
                style={{ fontSize: '28px', fontVariationSettings: "'FILL' 1" }}
              >
                lock
              </span>
            </div>
            <h2
              className="text-headline-md text-primary tracking-tight"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              パスワードを設定
            </h2>
            <div className="h-0.5 w-16 mt-2 border-b-2 border-primary" />
          </div>

          <StepIndicator step={step} />

          <form onSubmit={handlePasswordNext} className="space-y-4">
            <ErrorAlert message={error} />

            <div className="space-y-2">
              <FieldLabel htmlFor="password">パスワード</FieldLabel>
              <PasswordInput
                id="password"
                value={form.password}
                onChange={(e) => updateForm('password', e.target.value)}
                placeholder="8文字以上、大小英字・数字含む"
              />
            </div>

            <div className="space-y-2">
              <FieldLabel htmlFor="confirmPassword">パスワード確認</FieldLabel>
              <PasswordInput
                id="confirmPassword"
                value={form.confirmPassword}
                onChange={(e) => updateForm('confirmPassword', e.target.value)}
                placeholder="パスワード再入力"
              />
            </div>

            {/* パスワード要件チェックリスト */}
            {form.password && (
              <div
                className="rounded-lg p-3 space-y-1.5 border-2 border-outline-variant"
                style={{ backgroundColor: '#f2f3fb' }}
              >
                <p
                  className="text-xs mb-2"
                  style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, color: '#414751', letterSpacing: '0.08em', textTransform: 'uppercase' }}
                >
                  パスワード要件
                </p>
                {[
                  { ok: passwordChecks.length, label: '8文字以上' },
                  { ok: passwordChecks.uppercase, label: '大文字を含む (A-Z)' },
                  { ok: passwordChecks.lowercase, label: '小文字を含む (a-z)' },
                  { ok: passwordChecks.number, label: '数字を含む (0-9)' },
                  { ok: passwordChecks.match, label: 'パスワードが一致' },
                ].map(({ ok, label }) => (
                  <div key={label} className="flex items-center gap-2 text-xs">
                    <span
                      className="material-symbols-outlined"
                      style={{
                        fontSize: '16px',
                        color: ok ? '#216d1c' : '#717782',
                        fontVariationSettings: ok ? "'FILL' 1" : "'FILL' 0",
                      }}
                    >
                      {ok ? 'check_circle' : 'radio_button_unchecked'}
                    </span>
                    <span style={{ color: ok ? '#216d1c' : '#717782' }}>{label}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <AeroButton
                type="button"
                variant="secondary"
                onClick={() => setStep(STEPS.ACCOUNT)}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
                  arrow_back
                </span>
                <span>戻る</span>
              </AeroButton>
              <AeroButton type="submit" disabled={!isPasswordValid}>
                <span>次へ</span>
                <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform" style={{ fontSize: '20px' }}>
                  arrow_forward
                </span>
              </AeroButton>
            </div>
          </form>
        </QuestWindow>
      </PageShell>
    );
  }

  // ===== Step3: 利用形態選択 =====
  if (step === STEPS.ORG_TYPE) {
    return (
      <PageShell>
        <QuestWindow>
          <div className="flex flex-col items-center mb-6">
            <div
              className="p-4 rounded-full mb-4"
              style={{ backgroundColor: '#0068b7', boxShadow: '0 4px 0 0 #00508e' }}
            >
              <span
                className="material-symbols-outlined text-white"
                style={{ fontSize: '28px', fontVariationSettings: "'FILL' 1" }}
              >
                hub
              </span>
            </div>
            <h2
              className="text-headline-md text-primary tracking-tight"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              利用形態を選択
            </h2>
            <div className="h-0.5 w-16 mt-2 border-b-2 border-primary" />
          </div>

          <StepIndicator step={step} />

          <p className="text-sm text-on-surface-variant text-center mb-5" style={{ fontFamily: 'Inter, sans-serif' }}>
            後から変更することもできます
          </p>

          <form onSubmit={handleOrgTypeNext} className="space-y-4">
            <ErrorAlert message={error} />

            <div className="space-y-3">
              {/* 個人利用カード */}
              {[
                {
                  value: 'personal',
                  icon: 'person',
                  label: '個人として使う',
                  description: '自分だけでドローンを管理します。後から組織にアップグレードできます。',
                },
                {
                  value: 'organization',
                  icon: 'corporate_fare',
                  label: '組織として使う',
                  description: 'チームでドローンを管理します。メンバーを招待して共同管理できます。',
                },
              ].map(({ value, icon, label, description }) => {
                const isSelected = form.orgType === value;
                return (
                  <div
                    key={value}
                    className="flex items-start gap-4 p-4 rounded-xl cursor-pointer transition-all"
                    style={{
                      border: isSelected ? '4px solid #0068b7' : '2px solid #c1c7d3',
                      backgroundColor: isSelected ? '#f2f3fb' : '#ffffff',
                      boxShadow: isSelected ? '0 4px 0 0 #00508e' : 'none',
                    }}
                    onClick={() => updateForm('orgType', value)}
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: isSelected ? '#0068b7' : '#e6e8ef' }}
                    >
                      <span
                        className="material-symbols-outlined"
                        style={{
                          fontSize: '24px',
                          color: isSelected ? '#fff' : '#717782',
                          fontVariationSettings: isSelected ? "'FILL' 1" : "'FILL' 0",
                        }}
                      >
                        {icon}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div
                        className="font-medium"
                        style={{ fontFamily: 'Space Grotesk, sans-serif', color: isSelected ? '#00508e' : '#191c21' }}
                      >
                        {label}
                      </div>
                      <div className="text-sm mt-1" style={{ color: '#414751', fontFamily: 'Inter, sans-serif' }}>
                        {description}
                      </div>
                    </div>
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
                      style={{
                        border: isSelected ? 'none' : '2px solid #c1c7d3',
                        backgroundColor: isSelected ? '#0068b7' : 'transparent',
                      }}
                    >
                      {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 組織名入力 */}
            {form.orgType === 'organization' && (
              <div className="space-y-2">
                <FieldLabel htmlFor="orgName">組織名</FieldLabel>
                <AeroInput
                  id="orgName"
                  type="text"
                  value={form.orgName}
                  onChange={(e) => updateForm('orgName', e.target.value)}
                  placeholder="例：株式会社NTT e-Drone Technology"
                  autoFocus
                  icon="corporate_fare"
                />
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <div className="flex-1">
                <AeroButton
                  type="button"
                  variant="secondary"
                  onClick={() => setStep(STEPS.PASSWORD)}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>arrow_back</span>
                  <span>戻る</span>
                </AeroButton>
              </div>
              <div className="flex-1">
                <AeroButton
                  type="submit"
                  disabled={!form.orgType}
                  isLoading={isLoading}
                >
                  <span>登録する</span>
                  <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform" style={{ fontSize: '20px' }}>
                    how_to_reg
                  </span>
                </AeroButton>
              </div>
            </div>
          </form>
        </QuestWindow>
      </PageShell>
    );
  }

  // ===== Step4: メール確認コード =====
  if (step === STEPS.VERIFY) {
    return (
      <PageShell>
        <QuestWindow>
          <div className="flex flex-col items-center mb-6">
            <div
              className="p-4 rounded-full mb-4"
              style={{ backgroundColor: '#0068b7', boxShadow: '0 4px 0 0 #00508e' }}
            >
              <span
                className="material-symbols-outlined text-white"
                style={{ fontSize: '28px', fontVariationSettings: "'FILL' 1" }}
              >
                mark_email_unread
              </span>
            </div>
            <h2
              className="text-headline-md text-primary tracking-tight"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              メール確認
            </h2>
            <div className="h-0.5 w-16 mt-2 border-b-2 border-primary" />
          </div>

          <StepIndicator step={step} />

          <p className="text-sm text-on-surface-variant text-center mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
            <span className="font-medium" style={{ color: '#191c21' }}>{form.email}</span> に確認コードを送信しました
          </p>

          <form onSubmit={handleVerify} className="space-y-5">
            <ErrorAlert message={error} />

            <div className="space-y-2">
              <FieldLabel htmlFor="verificationCode">確認コード</FieldLabel>
              <AeroInput
                id="verificationCode"
                type="text"
                value={form.verificationCode}
                onChange={(e) => updateForm('verificationCode', e.target.value)}
                placeholder="6桁のコードを入力"
                maxLength={6}
                autoFocus
                icon="pin"
                inputClassName="text-center text-2xl tracking-[0.3em]"
              />
            </div>

            <div className="pt-2">
              <AeroButton type="submit" isLoading={isLoading}>
                <span>確認する</span>
                <span className="material-symbols-outlined" style={{ fontSize: '20px', fontVariationSettings: "'FILL' 1" }}>
                  verified
                </span>
              </AeroButton>
            </div>

            <div className="text-center text-sm" style={{ color: '#414751', fontFamily: 'Inter, sans-serif' }}>
              コードが届かない場合は{' '}
              <button
                type="button"
                className="font-medium hover:underline decoration-2 underline-offset-4"
                style={{ color: '#0068b7' }}
                onClick={handleResendCode}
              >
                再送信
              </button>
            </div>
          </form>
        </QuestWindow>
      </PageShell>
    );
  }

  // ===== Step5: 登録完了 =====
  return (
    <PageShell>
      <QuestWindow>
        <div className="flex flex-col items-center text-center">
          <div
            className="p-5 rounded-full mb-5"
            style={{ backgroundColor: '#216d1c', boxShadow: '0 5px 0 0 #005304' }}
          >
            <span
              className="material-symbols-outlined text-white"
              style={{ fontSize: '40px', fontVariationSettings: "'FILL' 1" }}
            >
              flight_takeoff
            </span>
          </div>

          <h2
            className="text-headline-md tracking-tight mb-2"
            style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#216d1c' }}
          >
            登録完了！
          </h2>
          <div className="h-0.5 w-16 mb-5 border-b-2 border-dashed" style={{ borderColor: '#216d1c' }} />

          <p className="text-on-surface-variant mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>
            {form.orgType === 'organization'
              ? `「${form.orgName}」として登録されました`
              : '個人アカウントが作成されました'}
          </p>

          <div
            className="w-full rounded-lg p-4 mb-6 border-2 border-outline-variant text-left"
            style={{ backgroundColor: '#f2f3fb' }}
          >
            <p className="text-sm text-on-surface" style={{ fontFamily: 'Inter, sans-serif' }}>
              ログインしてドローン管理を始めましょう。
            </p>
            {form.orgType === 'personal' && (
              <div className="flex items-start gap-2 mt-3 pt-3 border-t-2 border-dashed border-outline-variant">
                <span className="material-symbols-outlined text-primary" style={{ fontSize: '16px', marginTop: '1px' }}>info</span>
                <p className="text-xs text-on-surface-variant" style={{ fontFamily: 'Inter, sans-serif' }}>
                  設定画面からいつでも組織にアップグレードできます
                </p>
              </div>
            )}
          </div>

          <AeroButton onClick={() => navigate('/login')}>
            <span>ログインへ</span>
            <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>
              login
            </span>
          </AeroButton>
        </div>
      </QuestWindow>
    </PageShell>
  );
}
